import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { dateTime } from '../lib/format';
import { PlaceholderTag } from '../components/lumina/Placeholder';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Rascunho',
  sending: 'Enviando…',
  sent: 'Enviada',
  failed: 'Falhou',
};

const STATUS_CLS: Record<string, string> = {
  draft: 'bg-surface-variant text-outline',
  sending: 'bg-[#f59e0b]/15 text-[#f59e0b]',
  sent: 'bg-secondary-container/25 text-secondary',
  failed: 'bg-error/15 text-error',
};

const FIELD =
  'w-full px-space-md py-space-sm rounded-lg bg-surface-container-lowest text-on-surface font-body-md text-body-md placeholder:text-outline focus:outline-none focus:bg-surface-container transition-all shadow-inner';

/** Automações & Bots — redesign Lumina (docs/prototype/storeo_automa_es_bots_telegram_e_mail).
 *  Dado real: campaigns (histórico) + createCampaign (broadcast Telegram). Config do bot,
 *  régua de recuperação de carrinho e e-mail transacional = placeholder — sem API. */
export function Campanhas() {
  const [creating, setCreating] = useState(false);
  const campaigns = useQuery({ queryKey: ['campaigns'], queryFn: () => api.campaigns() });

  return (
    <div className="lumina flex flex-col gap-space-lg text-on-surface">
      <div className="flex flex-col gap-space-3xs">
        <div className="flex items-center gap-space-xs">
          <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">Crescimento &amp; Marketing</span>
          <span className="text-outline text-label-sm">•</span>
          <span className="font-label-sm text-label-sm text-secondary flex items-center gap-space-3xs">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-ping" /> Bots online
          </span>
        </div>
        <h1 className="font-headline-lg text-headline-lg text-on-surface tracking-tight">Automações &amp; Bots</h1>
        <p className="font-body-md text-body-md text-on-surface-variant">
          Recuperação de carrinho, e-mail transacional e broadcast para o seu grupo do Telegram.
        </p>
      </div>

      {/* automation panels — placeholder */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-space-md">
        {[
          { icon: 'send', color: 'bg-secondary/15 text-secondary', title: 'Bot de Recuperação (Telegram)', sub: '84 abordagens • 32 convertidos (38%)', tag: 'Ativo' },
          { icon: 'mark_email_read', color: 'bg-primary/15 text-primary', title: 'Régua de E-mail Transacional', sub: 'Pedido, PIX pendente, boas-vindas', tag: 'Ativo' },
          { icon: 'notifications_active', color: 'bg-tertiary/15 text-tertiary', title: 'Alerta de venda no seu Telegram', sub: 'Vibra a cada venda aprovada', tag: 'Sinc.' },
        ].map((a) => (
          <div key={a.title} className="rounded-2xl bg-surface-container-low p-space-lg shadow-md flex flex-col gap-space-sm">
            <div className="flex items-center justify-between">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${a.color}`}>
                <MSym name={a.icon} className="text-[22px]" />
              </div>
              <span className="px-2 py-0.5 rounded bg-secondary-container/20 text-secondary font-label-sm text-label-sm font-bold flex items-center gap-1">
                {a.tag}
                <PlaceholderTag />
              </span>
            </div>
            <span className="font-label-lg text-label-lg text-on-surface font-bold">{a.title}</span>
            <span className="font-body-sm text-body-sm text-on-surface-variant">{a.sub}</span>
          </div>
        ))}
      </div>

      {/* broadcast — real */}
      <div className="flex flex-col gap-space-md">
        <div className="flex items-center justify-between">
          <h2 className="font-headline-sm text-headline-sm text-on-surface font-bold">Campanhas / Broadcast no Telegram</h2>
          <button
            onClick={() => setCreating((v) => !v)}
            className="flex items-center gap-space-xs px-space-md py-space-xs rounded-xl bg-primary text-on-primary font-label-md text-label-md font-bold"
          >
            <MSym name={creating ? 'close' : 'add'} className="text-[18px]" />
            {creating ? 'Fechar' : 'Nova campanha'}
          </button>
        </div>

        {creating ? (
          <div className="rounded-2xl bg-surface-container-low p-space-lg shadow-md">
            <NewCampaignForm onCreated={() => setCreating(false)} />
          </div>
        ) : null}

        <div className="rounded-2xl bg-surface-container overflow-hidden shadow-lg">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse font-body-sm text-body-sm">
              <thead>
                <tr className="bg-surface-container-highest/40 text-on-surface font-label-sm text-label-sm uppercase tracking-wider font-semibold">
                  <th className="py-space-md px-space-lg">Mensagem</th>
                  <th className="py-space-md px-space-md">Público</th>
                  <th className="py-space-md px-space-md text-right">Entregues</th>
                  <th className="py-space-md px-space-md text-right">Falhas</th>
                  <th className="py-space-md px-space-md">Status</th>
                  <th className="py-space-md px-space-lg">Quando</th>
                </tr>
              </thead>
              <tbody className="text-on-surface">
                {!campaigns.data?.length ? (
                  <tr>
                    <td colSpan={6} className="py-space-2xl text-center text-on-surface-variant">Nenhuma campanha ainda.</td>
                  </tr>
                ) : (
                  campaigns.data.map((c) => (
                    <tr key={c.id} className="hover:bg-surface-container-high/60 transition-colors border-t border-surface-container-highest/30">
                      <td className="py-space-md px-space-lg text-on-surface-variant max-w-md truncate">{c.text}</td>
                      <td className="py-space-md px-space-md text-on-surface-variant">{c.audience === 'all' ? 'Todos' : 'Ativos'}</td>
                      <td className="py-space-md px-space-md text-right">
                        {c.deliveredCount}/{c.recipientCount}
                      </td>
                      <td className="py-space-md px-space-md text-right">{c.failedCount || '—'}</td>
                      <td className="py-space-md px-space-md">
                        <span className={`px-2 py-0.5 rounded-full font-label-sm text-label-sm font-bold ${STATUS_CLS[c.status] ?? 'bg-surface-variant text-outline'}`}>
                          {STATUS_LABEL[c.status] ?? c.status}
                        </span>
                      </td>
                      <td className="py-space-md px-space-lg text-outline">{dateTime(c.sentAt ?? c.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewCampaignForm({ onCreated }: { onCreated: () => void }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [link, setLink] = useState('');
  const [audience, setAudience] = useState('active');
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.createCampaign({ text, link: link || undefined, audience }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      onCreated();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível enviar'),
  });

  return (
    <form
      className="flex flex-col gap-space-md"
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      <label className="flex flex-col gap-space-2xs">
        <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">Mensagem</span>
        <textarea className={FIELD} rows={4} required value={text} onChange={(e) => setText(e.target.value)} />
      </label>
      <label className="flex flex-col gap-space-2xs">
        <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">Link (opcional)</span>
        <input className={FIELD} value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" />
      </label>
      <label className="flex flex-col gap-space-2xs">
        <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">Para quem</span>
        <select className={FIELD} value={audience} onChange={(e) => setAudience(e.target.value)}>
          <option value="active">Assinantes/clientes ativos</option>
          <option value="all">Todos que já entraram no grupo</option>
        </select>
      </label>
      {error ? (
        <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">{error}</p>
      ) : null}
      <button
        className="self-start px-space-lg py-space-xs rounded-xl bg-primary text-on-primary font-label-lg text-label-lg font-bold disabled:opacity-60"
        type="submit"
        disabled={create.isPending}
      >
        {create.isPending ? 'Enviando…' : 'Enviar campanha'}
      </button>
    </form>
  );
}
